import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  DollarSign,
  FileText,
  XCircle,
  AlertTriangle,
  Gift,
  TrendingUp,
  BookOpen,
  Building2,
  Receipt,
  ArrowUpDown,
  CheckCircle
} from 'lucide-react';
import type { SupplementDetailed } from '@/api/generated/client/models';
import type { DisplayTotals, DisplayRoom } from '@/types/supplement';
import { useCategoryStatistics, useFinancialTotals, useJustificationCounts } from '@/hooks/useSupplementStatistics';
import { CategoryStatCard } from '../summary/CategoryStatCard';
import { FinancialCard } from '../summary/FinancialCard';
import { JustificationStat } from '../summary/JustificationStat';

interface SupplementSummaryProps {
  supplement: SupplementDetailed;
  totals: DisplayTotals;
  rooms: DisplayRoom[];
}

export function SupplementSummary({ supplement, rooms }: SupplementSummaryProps) {
  // Use hooks to calculate statistics
  const categoryStats = useCategoryStatistics(rooms);
  const financialTotals = useFinancialTotals(categoryStats, rooms);
  const justificationCounts = useJustificationCounts(supplement);

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-20">
      {/* Supplement Analysis Details Card */}
      <Card className="relative overflow-hidden border-0 shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/20 via-transparent to-orange-50/20 dark:from-indigo-950/10 dark:to-orange-950/10" />
        <CardHeader className="relative pb-4">
          <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500/10 to-orange-500/10">
              <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            Supplement Analysis Details
          </CardTitle>
        </CardHeader>
        <Separator className="mb-6" />
        <CardContent className="relative space-y-8">
          {/* Item Categories Section */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Item Categories
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <CategoryStatCard
                icon={XCircle}
                label="Omitted Items"
                count={categoryStats.omitted.count}
                amount={categoryStats.omitted.amount}
                description="Items in contractor estimate not found in insurance estimate"
                borderColor="border-red-500"
                iconColor="text-red-600"
              />
              <CategoryStatCard
                icon={AlertTriangle}
                label="Under-Scoped Items"
                count={categoryStats.underscoped.count}
                amount={categoryStats.underscoped.rcvDifference}
                description="Contractor pricing exceeds insurance estimate for matched items"
                borderColor="border-orange-500"
                iconColor="text-orange-600"
              />
              <CategoryStatCard
                icon={Gift}
                label="Free-Scoped Items"
                count={categoryStats.freeScoped.count}
                amount={categoryStats.freeScoped.amount}
                description="Items in insurance estimate not found in contractor estimate"
                borderColor="border-blue-500"
                iconColor="text-blue-600"
              />
              <CategoryStatCard
                icon={TrendingUp}
                label="Over-Scoped Items"
                count={categoryStats.overscoped.count}
                amount={categoryStats.overscoped.rcvDifference}
                description="Insurance estimate exceeds contractor pricing for matched items"
                borderColor="border-purple-500"
                iconColor="text-purple-600"
              />
              <CategoryStatCard
                icon={CheckCircle}
                label="Perfect Match"
                count={categoryStats.perfectMatch.count}
                amount={categoryStats.perfectMatch.amount}
                description="Matched items with identical pricing between estimates"
                borderColor="border-green-500"
                iconColor="text-green-600"
              />
            </div>
          </div>

          {/* Justification Statistics */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
              Justification Statistics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <JustificationStat
                icon={BookOpen}
                title="Code-Based Justifications"
                count={justificationCounts.codeBasedCount}
                description="Justifications with specific code references"
                iconBgColor="bg-blue-100 dark:bg-blue-950"
                iconTextColor="text-blue-600 dark:text-blue-400"
              />
              <JustificationStat
                icon={FileText}
                title="General Justifications"
                count={justificationCounts.generalCount}
                description="Justifications without code citations"
                iconBgColor="bg-slate-100 dark:bg-slate-950"
                iconTextColor="text-slate-600 dark:text-slate-400"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Financial Analysis Card */}
      <Card className="relative overflow-hidden border-0 shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50/30 via-transparent to-blue-50/30 dark:from-green-950/20 dark:to-blue-950/20" />
        <CardHeader className="relative pb-4">
          <CardTitle className="flex items-center gap-3 text-lg sm:text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-br from-green-500/10 to-blue-500/10">
              <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            Financial Analysis
          </CardTitle>
        </CardHeader>
        <Separator className="mb-6" />
        <CardContent className="relative space-y-6">
          {/* Main Financial Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <FinancialCard
              title="Insurance Total"
              amount={financialTotals.insuranceTotal}
              icon={Receipt}
              variant="default"
            />
            <FinancialCard
              title="Contractor Total"
              amount={financialTotals.contractorTotal}
              icon={Building2}
              variant="default"
            />
            <FinancialCard
              title="RCV Difference"
              amount={financialTotals.rcvDifference}
              icon={ArrowUpDown}
              variant="highlight"
            />
          </div>

          {/* Supplement Amount Banner */}
          <div
            className={
              financialTotals.supplementPrice > 0
                ? "relative overflow-hidden rounded-lg border-0 shadow-md bg-green-50/50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 p-4 sm:p-6"
                : "relative overflow-hidden rounded-lg border-0 shadow-md bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 p-4 sm:p-6"
            }
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div
                className={
                  financialTotals.supplementPrice > 0
                    ? "p-3 rounded-lg bg-green-100 dark:bg-green-900/50"
                    : "p-3 rounded-lg bg-white/20 backdrop-blur-sm"
                }
              >
                <DollarSign
                  className={
                    financialTotals.supplementPrice > 0
                      ? "h-8 w-8 text-green-600 dark:text-green-400"
                      : "h-8 w-8 text-muted-foreground"
                  }
                />
              </div>
              <div className="text-left sm:text-right">
                <p
                  className={
                    financialTotals.supplementPrice > 0
                      ? "text-sm font-medium text-green-700 dark:text-green-300 uppercase tracking-wide"
                      : "text-sm font-medium text-muted-foreground uppercase tracking-wide"
                  }
                >
                  Total Supplement Amount
                </p>
                <p
                  className={
                    financialTotals.supplementPrice > 0
                      ? "mt-1 text-3xl sm:text-4xl font-bold text-green-600 dark:text-green-400"
                      : "mt-1 text-3xl sm:text-4xl font-bold text-foreground"
                  }
                >
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                  }).format(financialTotals.supplementPrice / 100)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
